import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CorporatePage() {
    const { t } = useTranslation(['common', 'corporate']);

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Section */}
            <section className="relative py-24 bg-sage-900 text-white overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop')] bg-cover bg-center opacity-20"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-6xl font-display font-bold mb-6"
                    >
                        {t('corporate.hero.title')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto"
                    >
                        {t('corporate.hero.subtitle')}
                    </motion.p>
                </div>
            </section>

            {/* Mission Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-sage-100 text-sage-700 text-sm font-semibold mb-4">
                            {t('corporate.mission.badge')}
                        </span>
                        <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                            {t('corporate.mission.title')}
                        </h2>
                        <p className="text-lg text-gray-600 leading-relaxed">
                            {t('corporate.mission.description')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Philosophy / Values Section */}
            <section className="py-20 bg-sage-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-terra-100 text-terra-700 text-sm font-semibold mb-4">
                            {t('corporate.philosophy.badge')}
                        </span>
                        <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                            {t('corporate.philosophy.title')}
                        </h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            {t('corporate.philosophy.description')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center text-sage-600 text-2xl mb-6">
                                <i className="ri-scales-3-line"></i>
                            </div>
                            <h3 className="text-xl font-bold mb-3">{t('corporate.philosophy.values.fair.title')}</h3>
                            <p className="text-gray-600">{t('corporate.philosophy.values.fair.desc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-terra-100 rounded-xl flex items-center justify-center text-terra-600 text-2xl mb-6">
                                <i className="ri-map-pin-heart-line"></i>
                            </div>
                            <h3 className="text-xl font-bold mb-3">{t('corporate.philosophy.values.local.title')}</h3>
                            <p className="text-gray-600">{t('corporate.philosophy.values.local.desc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 text-2xl mb-6">
                                <i className="ri-team-line"></i>
                            </div>
                            <h3 className="text-xl font-bold mb-3">{t('corporate.philosophy.values.community.title')}</h3>
                            <p className="text-gray-600">{t('corporate.philosophy.values.community.desc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold mb-4">
                            {t('corporate.team.badge')}
                        </span>
                        <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                            {t('corporate.team.title')}
                        </h2>
                        <p className="text-lg text-gray-600">{t('corporate.team.subtitle')}</p>
                    </div>

                    {/* Le Créateur */}
                    <div className="text-center group md:col-start-2">
                        <div className="relative w-56 h-56 mx-auto mb-6 rounded-full overflow-hidden shadow-xl border-4 border-white transition-transform duration-300 group-hover:scale-105">
                            <img
                                src="/pluk.jpg"
                                alt={t('corporate.team.members.ceo.name')}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <h3 className="text-2xl font-display font-bold mb-2 text-gray-900">{t('corporate.team.members.ceo.name')}</h3>
                        <p className="text-terra-600 font-bold mb-4 uppercase tracking-wider text-sm">{t('corporate.team.members.ceo.role')}</p>
                        <p className="text-gray-600 leading-relaxed max-w-lg mx-auto italic">
                            "{t('corporate.team.members.ceo.bio')}"
                        </p>
                    </div>
                </div>
            </section>

            {/* Investors Section */}
            <section className="py-24 bg-gray-900 text-white text-center">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-semibold mb-6">
                        {t('corporate.investors.badge')}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
                        {t('corporate.investors.title')}
                    </h2>
                    <p className="text-xl text-white/80 mb-10 leading-relaxed">
                        {t('corporate.investors.description')}
                    </p>
                    <a
                        href="mailto:investors@traveltrok.com"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors"
                    >
                        {t('corporate.investors.cta')}
                        <i className="ri-arrow-right-line"></i>
                    </a>
                </div>
            </section>

            <Footer />
        </div>
    );
}
